import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowUp, ArrowDown, Loader2, Sparkles, ChevronRight } from 'lucide-react';
import { SentimentPill } from '@/components/CommunityPostBits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return '';
  }
}

function PortfolioPostRow({ post }) {
  const tickerHref = `/community?tab=feed&ticker=${encodeURIComponent(post.ticker)}`;
  const scoreNum = post.score || 0;
  const arrow = scoreNum > 0
    ? <ArrowUp className="w-3 h-3 text-orange-500" />
    : scoreNum < 0
    ? <ArrowDown className="w-3 h-3 text-violet-500" />
    : <ArrowUp className="w-3 h-3 text-muted-foreground" />;
  const scoreCls = scoreNum > 0
    ? 'text-xs font-bold tabular-nums text-orange-500'
    : scoreNum < 0
    ? 'text-xs font-bold tabular-nums text-violet-500'
    : 'text-xs font-bold tabular-nums text-muted-foreground';
  return (
    <Link
      to={tickerHref}
      className="block rounded-lg border border-border bg-card hover:border-violet-300 dark:hover:border-violet-700 transition px-3 py-2.5"
      data-testid={`portfolio-post-${post.post_id}`}
    >
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-1 flex-wrap">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 rounded font-bold text-xs">
          {post.ticker}
        </span>
        <SentimentPill label={post.sentiment_label} score={post.sentiment_score} />
        <span>·</span>
        <span>@{post.handle}</span>
        <span>·</span>
        <span>{timeAgo(post.created_at)}</span>
        <span className="ml-auto flex items-center gap-1">
          {arrow}
          <span className={scoreCls}>{scoreNum}</span>
        </span>
      </div>
      <p className="text-sm font-semibold line-clamp-2">{post.title}</p>
      <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <MessageCircle className="w-3 h-3" />
          {post.comment_count} {post.comment_count === 1 ? 'comment' : 'comments'}
        </span>
      </div>
    </Link>
  );
}

export default function MyPortfolioCommunityPosts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/community/posts/about-my-portfolio`, { params: { limit: 5, days: 14 }, withCredentials: true })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Hide quietly when no positions OR no posts about them
  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-card p-4 text-xs text-muted-foreground flex items-center gap-2" data-testid="my-portfolio-posts-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking for community chatter on your tickers…
      </div>
    );
  }
  if (!data || !data.has_positions || !data.posts || data.posts.length === 0) {
    return null;
  }

  return (
    <div className="rounded-2xl border border-violet-200 dark:border-violet-800 bg-gradient-to-br from-violet-50/60 via-card to-card dark:from-violet-950/30 dark:via-card dark:to-card p-4 sm:p-5" data-testid="my-portfolio-community-posts">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-violet-100 dark:bg-violet-950/60 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <h2 className="text-sm sm:text-base font-bold leading-tight">Community chatter on your portfolio</h2>
            <p className="text-[11px] text-muted-foreground">
              Top posts about tickers you hold · last 14 days
            </p>
          </div>
        </div>
        <Link
          to="/community?tab=feed"
          className="text-xs text-violet-600 dark:text-violet-400 font-semibold hover:underline flex items-center gap-0.5"
          data-testid="my-portfolio-posts-see-all"
        >
          See all <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="space-y-2">
        {data.posts.map((p) => (
          <PortfolioPostRow key={p.post_id} post={p} />
        ))}
      </div>
    </div>
  );
}
